import { useState } from "react";
import { CalendarClock, DollarSign } from "lucide-react";

import { useCalendar } from "@/features/availability/model/useAvailability";
import { WeeklyTemplateEditor } from "@/features/availability/ui/WeeklyTemplateEditor";
import { StepFooter } from "@/features/onboarding/ui/IdentityStep";
import { useUpdateWorkerProfile } from "@/features/workers/model/useWorkerProfile";
import type { WorkerProfile } from "@/features/workers/types";

interface Props {
  profile: WorkerProfile;
  onSaved: () => void;
  onBack: () => void;
}

export function AvailabilityStep({ profile, onSaved, onBack }: Props) {
  const update = useUpdateWorkerProfile();
  const { data: calendar, isLoading } = useCalendar();

  const [rate, setRate] = useState<string>(() => {
    if (!profile.min_hourly_rate) return "";
    const n = Number(profile.min_hourly_rate);
    return n > 0 ? String(n) : "";
  });
  const [error, setError] = useState<string | null>(null);

  const onContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (rate.trim() !== "") {
      const value = Number(rate);
      if (!Number.isFinite(value) || value < 0 || value > 999) {
        setError("Enter a rate between 0 and 999.");
        return;
      }
      await update.mutateAsync({ min_hourly_rate: value.toFixed(2) });
    }
    onSaved();
  };

  return (
    <form onSubmit={onContinue} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Availability & rate
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Optional — both help us show you only the shifts that fit. You can
          change either of these later from your dashboard.
        </p>
      </div>

      <section className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-copy">
          <DollarSign className="h-4 w-4 text-copy-muted" />
          Minimum hourly rate
          <span className="text-xs font-normal text-copy-muted">(optional)</span>
        </div>
        <p className="text-xs text-copy-muted">
          We'll hide any shift that pays below this. Leave blank to see every
          shift you're qualified for.
        </p>
        <div className="relative max-w-[180px]">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-copy-muted">
            $
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={999}
            step="0.50"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="20.00"
            className="w-full rounded-md border border-border bg-surface py-2 pl-7 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-copy-muted">
            /hr
          </span>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-copy">
          <CalendarClock className="h-4 w-4 text-copy-muted" />
          Weekly availability
          <span className="text-xs font-normal text-copy-muted">(optional)</span>
        </div>
        <p className="text-xs text-copy-muted">
          The hours you're typically free. Date-specific exceptions (vacation,
          school) live on your dashboard once you're in.
        </p>
        {isLoading || !calendar ? (
          <div className="rounded-md border border-border bg-surface p-3 text-xs text-copy-muted">
            Loading your calendar…
          </div>
        ) : (
          <WeeklyTemplateEditor template={calendar.template} />
        )}
      </section>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <StepFooter
        onBack={onBack}
        isSubmitting={update.isPending}
        nextLabel="Next"
      />
    </form>
  );
}
